// Create a deep nested edit view for Slider and its translated items
import React, { useEffect, useState } from 'react';
import { 
    Form, 
    Input, 
    InputNumber, 
    Switch, 
    Button, 
    Card, 
    Space, 
    message, 
    Tabs, 
    Col, 
    Row, 
    Upload, 
    Typography, 
    Image 
} from 'antd';
import { 
    UploadOutlined, 
    PlusOutlined, 
    DeleteOutlined, 
    ArrowLeftOutlined, 
    PictureOutlined 
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../api/axios';
import MediaPickerModal from '../../components/common/MediaPickerModal';

const { Title } = Typography;

const SliderEdit: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isNew = id === 'new';
    const [form] = Form.useForm();
    const queryClient = useQueryClient();
    const appLanguages = ['de', 'en', 'tr', 'ru', 'ar'];

    const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
    const [selectedFieldForMedia, setSelectedFieldForMedia] = useState<number | null>(null);

    const { data: sliderData, isLoading } = useQuery({
        queryKey: ['adminSlider', id],
        queryFn: async () => {
            const response = await apiClient.get(`/admin/sliders/${id}`);
            return response.data.data;
        },
        enabled: !isNew
    });

    useEffect(() => {
        if (sliderData) {
            // Transform data structure to match form structure
            const formValues = {
                name: sliderData.name,
                autoplaySpeed: sliderData.autoplaySpeed,
                showDots: sliderData.showDots,
                showArrows: sliderData.showArrows,
                items: sliderData.items?.map((item: any) => ({
                    id: item.id,
                    imageUrl: item.imageUrl,
                    orderIndex: item.orderIndex,
                    translations: item.translations
                })) || []
            };
            form.setFieldsValue(formValues);
        } else if (isNew) {
            form.setFieldsValue({
                autoplaySpeed: 5000,
                showDots: true,
                showArrows: true,
                items: []
            });
        }
    }, [sliderData, isNew, form]);

    const mutation = useMutation({
        mutationFn: async (values: any) => {
            // Transform form items for submission
            const payload = {
                ...values,
                items: values.items?.map((item: any) => ({
                    id: item.id || null,
                    imageUrl: item.imageUrl,
                    orderIndex: item.orderIndex || 0,
                    translations: appLanguages.reduce((acc: any, lang) => {
                        acc[lang] = item.translations?.[lang] || {};
                        return acc;
                    }, {})

                })) || []
            };

            if (isNew) {
                const res = await apiClient.post('/admin/sliders', payload);
                return res.data;
            } else {
                const res = await apiClient.put(`/admin/sliders/${id}`, { id, ...payload });
                return res.data;
            }
        },
        onSuccess: () => {
            message.success('Slider erfolgreich gespeichert');
            queryClient.invalidateQueries({ queryKey: ['adminSliders'] });
            navigate('/admin/sliders');
        },
        onError: () => {
            message.error('Fehler beim Speichern');
        }
    });

    const onFinish = (values: any) => {
        mutation.mutate(values);
    };

    const setImageUrlForField = (fieldName: number, imgUrl: string) => {
        // 1. Direct form field value update for that specific Form.List item
        form.setFieldValue(['items', fieldName, 'imageUrl'], imgUrl);
        // 2. Clear any validation error and set value explicitly
        form.setFields([
            {
                name: ['items', fieldName, 'imageUrl'],
                value: imgUrl,
                errors: [],
            }
        ]);
        // 3. Update clone array so form state matches
        const currentItems = form.getFieldValue('items') || [];
        const updatedItems = [...currentItems];
        updatedItems[fieldName] = { ...updatedItems[fieldName], imageUrl: imgUrl };
        form.setFieldsValue({ items: updatedItems });
    };

    const handleCustomRequest = async (options: any, fieldName: number) => {
        const { file, onSuccess, onError } = options;
        const formData = new FormData();
        formData.append('file', file);
        formData.append('isPublic', 'true');
        formData.append('isActive', 'true');

        try {
            const response = await apiClient.post('/admin/media/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            const dataObj = response.data?.data || response.data?.Data || response.data;
            const imgUrl = dataObj?.url || dataObj?.Url || dataObj?.filePath || dataObj?.FilePath;
            
            if (!imgUrl) {
                throw new Error('Keine URL in der Antwort erhalten');
            }

            setImageUrlForField(fieldName, imgUrl);

            onSuccess('Ok');
            message.success('Bild hochgeladen und verknüpft');
            queryClient.invalidateQueries({ queryKey: ['adminMedia'] });
        } catch (error: any) {
            onError({ error });
            message.error(error.response?.data?.message || error.message || 'Upload fehlgeschlagen');
        }
    };

    const handleSelectMedia = (url: string) => {
        if (selectedFieldForMedia !== null) {
            setImageUrlForField(selectedFieldForMedia, url);
        }
        setMediaPickerOpen(false);
        setSelectedFieldForMedia(null);
    };

    if (isLoading && !isNew) return <div>Laden...</div>;

    return (
        <Card 
            title={
                <Space>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/sliders')} />
                    {isNew ? 'Neuen Slider erstellen' : 'Slider bearbeiten'}
                </Space>
            }
        >
            <Form form={form} layout="vertical" onFinish={onFinish}>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="name" label="Interner Name" rules={[{ required: true }]}>
                            <Input placeholder="z.B. Hero Homepage" />
                        </Form.Item>
                    </Col>
                    <Col span={4}>
                        <Form.Item name="autoplaySpeed" label="Autoplay Speed (ms)">
                            <InputNumber min={0} className="w-full" />
                        </Form.Item>
                    </Col>
                    <Col span={4}>
                        <Form.Item name="showDots" label="Punkte anzeigen" valuePropName="checked">
                            <Switch />
                        </Form.Item>
                    </Col>
                    <Col span={4}>
                        <Form.Item name="showArrows" label="Pfeile anzeigen" valuePropName="checked">
                            <Switch />
                        </Form.Item>
                    </Col>
                </Row>

                <Title level={4} className="mt-8 mb-4">Slides verwalten</Title>
                
                <Form.List name="items">
                    {(fields, { add, remove }) => (
                        <div className="space-y-6 flex flex-col gap-4">
                            {fields.map((field, index) => (
                                <Card size="small" key={field.key} className="bg-gray-50">
                                    <div className="flex justify-between items-center mb-4">
                                        <strong>Slide #{index + 1}</strong>
                                        <Button danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} />
                                    </div>
                                    
                                    <Form.Item name={[field.name, 'id']} hidden><Input /></Form.Item>
                                    
                                    <Row gutter={24}>
                                        <Col span={8}>
                                            <Form.Item 
                                                name={[field.name, 'imageUrl']} 
                                                label="Bild URL" 
                                                rules={[{ required: true, message: 'Bild ist erforderlich' }]}
                                            >
                                                <Input placeholder="https://... oder Bild hochladen / aus Mediathek wählen" />
                                            </Form.Item>
                                            
                                            <Space wrap className="mb-3">
                                                <Upload 
                                                    customRequest={(options) => handleCustomRequest(options, field.name)} 
                                                    showUploadList={false}
                                                >
                                                    <Button icon={<UploadOutlined />}>Bild Hochladen</Button>
                                                </Upload>
                                                <Button 
                                                    icon={<PictureOutlined />}
                                                    onClick={() => {
                                                        setSelectedFieldForMedia(field.name);
                                                        setMediaPickerOpen(true);
                                                    }}
                                                >
                                                    Aus Mediathek
                                                </Button>
                                            </Space>

                                            {/* Preview current slide image */}
                                            <Form.Item
                                                noStyle
                                                shouldUpdate={(prevValues, currentValues) =>
                                                    prevValues?.items?.[field.name]?.imageUrl !== currentValues?.items?.[field.name]?.imageUrl
                                                }
                                            >
                                                {() => {
                                                    const currentUrl = form.getFieldValue(['items', field.name, 'imageUrl']);
                                                    if (!currentUrl) return null;
                                                    return (
                                                        <div className="mt-2 mb-3">
                                                            <div className="text-xs text-gray-500 mb-1">Vorschau:</div>
                                                            <Image
                                                                src={currentUrl}
                                                                alt={`Slide #${index + 1}`}
                                                                style={{ maxHeight: 100, maxWidth: '100%', objectFit: 'cover', borderRadius: 4, border: '1px solid #d9d9d9' }}
                                                            />
                                                        </div>
                                                    );
                                                }}
                                            </Form.Item>

                                            <Form.Item name={[field.name, 'orderIndex']} label="Sortierung" className="mt-2">
                                                <InputNumber min={0} className="w-full" />
                                            </Form.Item>
                                        </Col>
                                        
                                        <Col span={16}>
                                             <Tabs
                                                items={appLanguages.map(lang => ({
                                                    key: lang,
                                                    label: lang === 'de' ? 'Deutsch' : lang === 'tr' ? 'Türkçe' : lang === 'en' ? 'English' : lang === 'ru' ? 'Русский' : 'العربية',
                                                    children: (
                                                        <div className="space-y-4">
                                                            <Form.Item name={[field.name, 'translations', lang, 'title']} label="Titel / Title">
                                                                <Input />
                                                            </Form.Item>
                                                            <Form.Item name={[field.name, 'translations', lang, 'subtitle']} label="Untertitel / Subtitle">
                                                                <Input />
                                                            </Form.Item>
                                                            <Row gutter={16}>
                                                                <Col span={12}>
                                                                    <Form.Item name={[field.name, 'translations', lang, 'buttonText']} label="Button Text">
                                                                        <Input />
                                                                    </Form.Item>
                                                                </Col>
                                                                <Col span={12}>
                                                                    <Form.Item name={[field.name, 'translations', lang, 'buttonUrl']} label="Button URL">
                                                                        <Input placeholder="/projekte" />
                                                                    </Form.Item>
                                                                </Col>
                                                            </Row>
                                                        </div>
                                                    )
                                                }))}
                                            />

                                        </Col>
                                    </Row>
                                </Card>
                            ))}
                            
                            <Button type="dashed" onClick={() => add({ orderIndex: fields.length + 1 })} block icon={<PlusOutlined />}>
                                Slide hinzufügen
                            </Button>
                        </div>
                    )}
                </Form.List>

                <div className="mt-8 flex justify-end">
                    <Button type="primary" htmlType="submit" size="large" loading={mutation.isPending}>
                        Speichern
                    </Button>
                </div>
            </Form>

            <MediaPickerModal
                open={mediaPickerOpen}
                onCancel={() => {
                    setMediaPickerOpen(false);
                    setSelectedFieldForMedia(null);
                }}
                onSelect={handleSelectMedia}
            />
        </Card>
    );
};

export default SliderEdit;
